import { RefObject, useCallback, useState } from 'react';
import Quill, { Delta } from 'quill';

import { useLatestRef } from '@/shared/hook';

import { enforceQuillMaxLength, registerCharacterLimitClipboardMatcher } from './util';

interface UseQuillCharacterLimitOptions {
  maxLength?: number;
  quillRef: RefObject<null | Quill>;
}

export function useQuillCharacterLimit({ maxLength, quillRef }: UseQuillCharacterLimitOptions) {
  const [characterCount, setCharacterCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const maxLengthRef = useLatestRef(maxLength);

  const getCurrentCharacterCount = useCallback(() => {
    if (!quillRef.current) {
      return 0;
    }

    return Math.max(0, quillRef.current.getText().length - 1);
  }, [quillRef]);

  const getCurrentSelectionLength = useCallback(() => {
    if (!quillRef.current) {
      return 0;
    }

    return quillRef.current.getSelection()?.length ?? 0;
  }, [quillRef]);

  const enforceMaxLengthIfNeeded = useCallback(
    (nextCount: number, changeDelta?: Delta): boolean => {
      if (typeof maxLength !== 'number' || nextCount <= maxLength || !quillRef.current) {
        return false;
      }

      setCharacterCount(enforceQuillMaxLength(quillRef.current, maxLength, changeDelta));

      return true;
    },
    [maxLength, quillRef],
  );

  const syncCharacterCount = useCallback(() => {
    setCharacterCount(getCurrentCharacterCount());
  }, [getCurrentCharacterCount]);

  const registerClipboardMatcher = useCallback(
    (quill: Quill) => {
      registerCharacterLimitClipboardMatcher(quill, {
        getCurrentLength: getCurrentCharacterCount,
        getCurrentSelectionLength,
        getMaxLength: () => maxLengthRef.current,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCurrentCharacterCount, getCurrentSelectionLength],
  );

  return {
    characterCount,
    enforceMaxLengthIfNeeded,
    getCurrentCharacterCount,
    isFocused,
    registerClipboardMatcher,
    setCharacterCount,
    setIsFocused,
    syncCharacterCount,
  };
}
